import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { usePurchases } from '../contexts/PurchasesContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  FontText,
  PrimaryButton,
  SecondaryButton,
  AndroidDownloadButton,
  AppleDownloadButton,
  SEOHead,
} from '../components/common';
import { ExitBrowserPrompt } from '../components/ExitBrowserPrompt';
import { logErrorsWithMessage } from '../utils/errors';
import { localAnalytics } from '../utils/analytics';
import { handleLastChapterRedirection } from '../utils/navigation';
import { supabase } from '../api/initSupabase';
import { APP_URL } from '../utils/constants';

type Step = 'registration' | 'mobile-app-prompt' | 'exit-prompt';

export const PurchaseFinalize: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const {
    isAnonymous,
    isAuthenticated,
    signUp,
    loading: authLoading,
    userId,
    isCampaign,
  } = useAuth();
  const { purchaseEmail } = usePurchases();

  const [email, setEmail] = useState(purchaseEmail || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState<Step>('registration');

  const isInAppBrowser = isCampaign;

  useEffect(() => {
    if (isAuthenticated && !isAnonymous) {
      if (isInAppBrowser) {
        setCurrentStep('exit-prompt');
      } else {
        if (localStorage.getItem('SHOULD_SEE_MOBILE_APP_PROMPT')) {
          setCurrentStep('mobile-app-prompt');
        } else {
          setLoading(true);
          handleLastChapterRedirection(userId!, navigate, true).finally(() => {
            setLoading(false);
          });
        }
      }
    } else {
      setCurrentStep('registration');
    }
  }, [isAuthenticated, isAnonymous]);

  const styles: Record<string, React.CSSProperties> = {
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      padding: 20,
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
    },
    formContainer: {
      backgroundColor: theme.colors.surface,
      padding: 40,
      borderRadius: 20,
      width: '100%',
      maxWidth: '400px',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
    },
    mobilePromptContainer: {
      backgroundColor: theme.colors.surface,
      padding: 40,
      borderRadius: 20,
      width: '100%',
      maxWidth: '500px',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
      textAlign: 'center',
    },
    successContainer: {
      backgroundColor: theme.colors.surface,
      padding: 40,
      borderRadius: 20,
      width: '100%',
      maxWidth: '400px',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
      textAlign: 'center',
    },
    title: {
      color: theme.colors.accent,
      textAlign: 'center',
      marginBottom: 10,
    },
    subtitle: {
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: 30,
    },
    mobilePromptTitle: {
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: 10,
    },
    mobilePromptSubtitle: {
      color: theme.colors.textSecondary,
      textAlign: 'center',
      marginBottom: 30,
    },
    successTitle: {
      color: theme.colors.accent,
      textAlign: 'center',
      marginBottom: 20,
    },
    successMessage: {
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: 30,
      lineHeight: 1.5,
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: 15,
    },
    downloadButtonsContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 15,
      marginBottom: 30,
    },
    actionsContainer: {
      display: 'flex',
      flexDirection: 'row',
      gap: 12,
      justifyContent: 'flex-end',
      marginBottom: 10,
      marginRight: -15,
      marginTop: -20,
    },
    input: {
      padding: '12px 16px',
      backgroundColor: theme.colors.overlay,
      border: `1px solid ${theme.colors.overlay}`,
      borderRadius: 8,
      color: theme.colors.text,
      outline: 'none',
      transition: 'border-color 0.3s',
    },
    passwordContainer: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    passwordInput: {
      padding: '12px 16px',
      paddingRight: 48,
      backgroundColor: theme.colors.overlay,
      border: `1px solid ${theme.colors.overlay}`,
      borderRadius: 8,
      color: theme.colors.text,
      outline: 'none',
      transition: 'border-color 0.3s',
      width: '100%',
    },
    eyeButton: {
      position: 'absolute',
      right: 12,
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: 4,
      color: theme.colors.textSecondary,
      fontSize: 18,
    },
  };

  useEffect(() => {
    if (purchaseEmail && !email) {
      setEmail(purchaseEmail);
    }
  }, [purchaseEmail, email]);

  useEffect(() => {
    localAnalytics().logEvent('PurchaseFinalizePageViewed', {
      isAnonymous,
      hasPurchaseEmail: !!purchaseEmail,
      isInAppBrowser,
      currentStep,
    });
  }, [currentStep]);

  const sendConfirmationEmail = async (userEmail: string) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: userEmail,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: APP_URL,
        },
      });

      if (error) {
        console.error('Error sending confirmation email:', error);
        localAnalytics().logEvent('PurchaseFinalizeEmailConfirmationError', {
          email: userEmail,
          error: error.message,
        });
        return false;
      }

      console.log('Confirmation email sent successfully');
      localAnalytics().logEvent('PurchaseFinalizeEmailConfirmationSent', {
        email: userEmail,
      });

      return true;
    } catch (err) {
      console.error('Exception sending confirmation email:', err);
      localAnalytics().logEvent('PurchaseFinalizeEmailConfirmationException', {
        email: userEmail,
        error: String(err),
      });
      return false;
    }
  };

  const handleRegistration = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t('passwords_dont_match'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('password_min_length'));
      return;
    }

    if (!email) {
      toast.error(t('email_required'));
      return;
    }

    setLoading(true);

    try {
      localAnalytics().logEvent('PurchaseFinalizeRegistrationAttempt', {
        email,
        hasPurchaseEmail: !!purchaseEmail,
      });

      localStorage.setItem('SHOULD_SEE_MOBILE_APP_PROMPT', 'true');
      const { error } = await signUp(email, password);

      if (error) {
        if (error.message === 'account_already_exists') {
          toast.error(t('account_already_exists'));
        } else if (error.message === 'invalid_email') {
          toast.error(t('invalid_email'));
        } else if (error.message === 'password_too_short') {
          toast.error(t('password_min_length'));
        } else if (
          error.message === 'update_failed' ||
          error.message === 'no_user_after_signup'
        ) {
          toast.error(t('registration_failed'));
        } else {
          logErrorsWithMessage(error, t('registration_failed'));
        }
      } else {
        localAnalytics().logEvent('PurchaseFinalizeRegistrationSuccess', {
          email,
        });

        setRegistrationComplete(true);
        void sendConfirmationEmail(email);
        setCurrentStep('mobile-app-prompt');
      }
    } catch (error) {
      logErrorsWithMessage(error, t('registration_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleMobileAppSkip = async () => {
    localAnalytics().logEvent('PurchaseFinalizeMobileAppSkipped', {
      isInAppBrowser,
    });
    localStorage.removeItem('SHOULD_SEE_MOBILE_APP_PROMPT');

    if (isInAppBrowser) {
      setCurrentStep('exit-prompt');
    } else {
      handleLastChapterRedirection(userId!, navigate, true);
    }
  };

  const handleAppDownload = (platform: 'ios' | 'android') => {
    localAnalytics().logEvent('PurchaseFinalizeMobileAppDownloadClicked', {
      platform,
      isInAppBrowser,
    });
  };

  const handleExitComplete = async () => {
    setLoading(true);
    try {
      await handleLastChapterRedirection(userId!, navigate, true);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <>
        <SEOHead
          title={t('purchase_finalize_title')}
          description={t('purchase_finalize_description')}
          canonical="/purchase-finalize"
        />
        <div style={styles.loadingContainer}>
          <FontText h3 style={{ color: theme.colors.text }}>
            {t('loading')}
          </FontText>
        </div>
      </>
    );
  }

  if (registrationComplete && currentStep === 'registration') {
    return (
      <>
        <SEOHead
          title={t('registration_successful')}
          description={t('registration_successful_description')}
          canonical="/purchase-finalize"
        />
        <div style={styles.container}>
          <div style={styles.successContainer}>
            <FontText h1 style={styles.successTitle}>
              {t('registration_successful')}
            </FontText>
            <FontText style={styles.successMessage}>
              {t('account_created_successfully')}
            </FontText>
            <FontText style={styles.successMessage}>
              {t('continue_to_mobile_app_prompt')}
            </FontText>
          </div>
        </div>
      </>
    );
  }

  if (currentStep === 'mobile-app-prompt') {
    return (
      <>
        <SEOHead
          title={t('mobile_app_download_title')}
          description={t('mobile_app_download_description')}
          canonical="/purchase-finalize"
        />
        <div style={styles.container}>
          <div style={styles.mobilePromptContainer}>
            <div style={styles.actionsContainer}>
              <SecondaryButton
                onClick={handleMobileAppSkip}
                style={{ borderRadius: 8 }}
              >
                <FontText>{t('skip')}</FontText>
              </SecondaryButton>
            </div>
            <FontText h1 style={styles.mobilePromptTitle}>
              {t('do_you_want_to_read_on_mobile_app')}
            </FontText>
            <FontText h4 style={styles.mobilePromptSubtitle}>
              {t('click_below_to_download_nanoreads_mobile_app')}
            </FontText>

            <div style={styles.downloadButtonsContainer}>
              <AppleDownloadButton
                userId={userId}
                onClick={() => handleAppDownload('ios')}
              />
              <AndroidDownloadButton
                userId={userId}
                onClick={() => handleAppDownload('android')}
              />
            </div>

            <div style={{ marginTop: 30, color: theme.colors.textSecondary }}>
              <FontText small>
                {t('questions_contact_nanoreads_support_at')}{' '}
                <a
                  href="mailto:book@nanoreads.com"
                  style={{ color: theme.colors.accent }}
                >
                  book@nanoreads.com
                </a>
              </FontText>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (currentStep === 'exit-prompt') {
    return (
      <>
        <SEOHead
          title={t('exit_browser_title')}
          description={t('exit_browser_description')}
          canonical="/purchase-finalize"
        />
        <ExitBrowserPrompt onClose={handleExitComplete} />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={t('complete_your_purchase')}
        description={t('complete_purchase_description')}
        canonical="/purchase-finalize"
      />
      <div style={styles.container}>
        <div style={styles.formContainer}>
          <FontText h1 style={styles.title}>
            {t('complete_your_purchase')}
          </FontText>
          <FontText h2 style={styles.subtitle}>
            {t('create_account_to_continue')}
          </FontText>

          <form onSubmit={handleRegistration} style={styles.form}>
            <input
              type="email"
              autoComplete="email"
              placeholder={t('email')}
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={styles.input}
              required
            />

            <div style={styles.passwordContainer}>
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder={t('password')}
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={styles.passwordInput}
                required
              />
              <button
                type="button"
                style={styles.eyeButton}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={
                  showPassword ? t('hide_password') : t('show_password')
                }
                tabIndex={-1}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>

            <div style={styles.passwordContainer}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder={t('confirm_password')}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                style={styles.passwordInput}
                required
              />
              <button
                type="button"
                style={styles.eyeButton}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={
                  showConfirmPassword ? t('hide_password') : t('show_password')
                }
                tabIndex={-1}
              >
                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>

            <PrimaryButton
              type="submit"
              disabled={loading}
              loading={loading}
              style={{ marginTop: 10, borderRadius: 8 }}
            >
              {loading ? t('loading') : t('create_account_and_continue')}
            </PrimaryButton>
          </form>
        </div>
      </div>
    </>
  );
};
